import {
  ChangeEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';

import type {
  IDropdownTagSelector,
  TDropdownTagSelectorOption,
} from '../../types/dropdown-tag-selector';
import type { ITagProps } from '../../types/tag/tag';

import { debounce } from '../../utils/debounce';
import { useClickOutside } from '../../hooks/useClickOutside';

import { Dropdown } from '../Dropdown';
import { DropdownButton } from '../Dropdown/DropdownButton';
import { DropdownMenu } from '../Dropdown/DropdownMenu';
import { DropdownMenuItem } from '../Dropdown/DropdownMenuItem';
import { Input } from '../Input';

import { ChevronDown } from '@unified-checkout/design-system/icons/index';

import styles from './dropdown-tag-selector.module.scss';

const DEFAULT_LOCALIZATION = {
  en: {
    emptyStateTitle: 'No results found',
    placeholder: 'Select options',
  },
  ar: {
    emptyStateTitle: 'لم يتم العثور على نتائج',
    placeholder: 'اختر الخيارات',
  },
};

export const DropdownTagSelector = ({
  isMultiple = true,
  isResetSearchQueryOnToggle = true,

  dropdownProps,
  dropdownMenuProps,
  renderDropdownMenuItem,

  optionsList,
  selectedOptions = [],
  onSelectionChange,
  onSearchQueryChange,

  tagsInputProps = {
    name: 'dropdown-tag-selector-input',
  },

  localization,
  lang = 'en',
  emptyState,
}: IDropdownTagSelector) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dropDownOpen, setDropDownOpen] = useState(false);

  const inputElementRef = useRef<
    HTMLInputElement & { handleSetInputValue: (value: string) => void }
  >(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputValueRef = useRef('');

  const localizedTexts = { ...DEFAULT_LOCALIZATION[lang], ...localization };

  const tags = useMemo(() => {
    return selectedOptions.map((option) => {
      if (tagsInputProps.getTagShapeProps) {
        return tagsInputProps.getTagShapeProps(option);
      }

      return {
        text: option.label,
        id: option.id,
      };
    });
  }, [selectedOptions, tagsInputProps.getTagShapeProps]);

  const searchItems = useMemo(() => {
    if (!searchQuery) return optionsList;
    const finalSearchKey = searchQuery.toLowerCase().trim();

    return optionsList.filter((item) => {
      return item.searchKey.toLowerCase().includes(finalSearchKey);
    });
  }, [searchQuery, optionsList]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchQuery(value);
      }, 200),
    []
  );

  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      inputValueRef.current = e.target.value;
      debouncedSetSearch(e.target.value);
      onSearchQueryChange?.(e.target.value);
    },
    [debouncedSetSearch]
  );

  const handleBackToInitialStatus = (initialSearchValue: string = '') => {
    setSearchQuery(initialSearchValue);
    inputValueRef.current = initialSearchValue;
    if (inputElementRef.current) {
      inputElementRef.current.handleSetInputValue(initialSearchValue);
    }
  };

  const handleToggleMenu = (open: boolean) => {
    setDropDownOpen(open);
    isResetSearchQueryOnToggle && handleBackToInitialStatus();
  };

  const handleRemove = useCallback(
    (selectedItem: TDropdownTagSelectorOption | ITagProps) => {
      const updated = selectedOptions.filter(
        (item) => item.id !== selectedItem.id
      );
      onSelectionChange?.(updated);
    },
    [selectedOptions, onSelectionChange]
  );

  const handleAdd = useCallback(
    (selectedItem: TDropdownTagSelectorOption) => {
      const exists = selectedOptions.some(
        (item) => item.id === selectedItem.id
      );
      if (!exists) {
        onSelectionChange?.([...selectedOptions, selectedItem]);
      }
    },
    [selectedOptions, onSelectionChange]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && dropDownOpen) {
      e.preventDefault();
      return;
    }

    if (
      e.key === 'Backspace' &&
      !inputValueRef.current.length &&
      selectedOptions.length
    ) {
      e.preventDefault();

      if (isMultiple) {
        const last = selectedOptions[selectedOptions.length - 1];
        handleRemove(last);
      }
    }
  };

  const handleOnMouseDownOrTouch = (e: React.MouseEvent | React.TouchEvent) => {
    if (inputElementRef.current?.contains(e.target as Node) || !isMultiple)
      return;

    // to prevent losing focus from the input when clicking on the container
    e.preventDefault();
    inputElementRef.current?.focus();
  };

  const renderItem = (item: TDropdownTagSelectorOption) => {
    if (renderDropdownMenuItem) {
      return renderDropdownMenuItem(item);
    }

    return (
      <p className={styles['dropdown-tag-selector__item']}>{item.label}</p>
    );
  };

  useClickOutside(
    wrapperRef,
    () => {
      setDropDownOpen(false);
      handleBackToInitialStatus();
    },
    dropDownOpen
  );

  return (
    <div
      className={styles['dropdown-tag-selector']}
      data-dropdown-open={dropDownOpen}
      onClick={() => {
        if (!dropDownOpen) {
          setDropDownOpen(true);
        }
      }}
      onMouseDown={handleOnMouseDownOrTouch}
      onTouchStart={handleOnMouseDownOrTouch}
      ref={wrapperRef}
    >
      <Dropdown
        {...dropdownProps}
        onToggleHandler={(open) => handleToggleMenu(open)}
      >
        <DropdownButton
          renderAs={
            <Input
              {...tagsInputProps}
              placeholder={
                tagsInputProps.placeholder || localizedTexts.placeholder
              }
              tags={tags}
              onTagRemove={handleRemove}
              type="tags"
              onChange={(e) => {
                !dropDownOpen && setDropDownOpen(true);
                handleSearch(e);
              }}
              ref={inputElementRef}
              trailingIcon={ChevronDown}
              isMultiple={isMultiple}
              tagDelimiters={[]}
              onKeyDown={handleKeyDown}
            />
          }
        />
        <DropdownMenu {...dropdownMenuProps} open={dropDownOpen}>
          {!searchItems.length
            ? emptyState || (
                <div className={styles['dropdown-tag-selector__no-results']}>
                  <p> {localizedTexts.emptyStateTitle}</p>
                </div>
              )
            : dropDownOpen &&
              searchItems.map((item) => {
                const itemKey = item.id;

                return (
                  <DropdownMenuItem
                    id={itemKey as string}
                    key={itemKey}
                    closeOnItemClick={false}
                    type={isMultiple ? 'checkbox' : 'default'}
                    onCheckboxItemClickHandler={(checked: boolean) => {
                      if (checked) handleAdd(item);
                      else handleRemove(item);
                      handleBackToInitialStatus();
                    }}
                    onClickHandler={() => {
                      if (isMultiple) return;
                      handleBackToInitialStatus();
                      onSelectionChange?.([item]);
                      setDropDownOpen(false);
                    }}
                    checked={selectedOptions.some(
                      (selected) => selected.id === item.id
                    )}
                    selected={
                      !isMultiple &&
                      selectedOptions.some(
                        (selected) => selected.id === item.id
                      )
                    }
                    render={renderItem(item)}
                  />
                );
              })}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};
